import { ReviewReason } from './Review';

export const trendRentalType = {
    HOURLY: 'hourly_rentals',
    FLAT_RATE: 'flat_rate_rentals',
    DAILY: 'daily_rentals',
    MONTHLY: 'monthly_rentals',
    TENANT: 'tenant_rentals',
    ENTERPRISE: 'enterprise_rentals',
} as const;

export type TrendRentalType = (typeof trendRentalType)[keyof typeof trendRentalType];

type Trend = {
    id: string;
    report_month: string; // YYYY-MM-DD
} & Record<TrendRentalType, number>;

export type DriverTrends = {
    trends: Trend[];
    avg_hourly_rentals: number;
    avg_daily_rentals: number;
    avg_monthly_rentals: number;
    avg_tenant_rentals: number;
    avg_enterprise_rentals: number;
    avg_flat_rate_rentals: number;
};

export enum InsightsPeriod {
    THIRTY_DAYS = 'L30D',
    NINETY_DAYS = 'L90D',
    SIX_MONTHS = 'L6M',
    TWELVE_MONTHS = 'L12M',
    ALL_TIME = 'ALL_TIME',
}

export enum TenantInsightsPeriod {
    THREE_MONTHS = 'L3M',
    SIX_MONTHS = 'L6M',
    TWELVE_MONTHS = 'L12M',
    TWENTY_FOUR_MONTHS = 'L24M',
}

export type DriverReviewsSummary = {
    sk: string;
    period: InsightsPeriod;
    total_reviews: number;
    avg_rating: number;
    five_star_count: number;
    four_star_count: number;
    three_star_count: number;
    two_star_count: number;
    one_star_count: number;
};

export type DriverReviewSample = {
    sk: string;
    period: InsightsPeriod;
    review_id: string;
    review_date: string;
    comment: string;
    rating: number;
    reason: string;
    gpt_sentiment_ranking: number;
    gpt_usefulness_ranking: number;
    recency_rank: number;
};

export type DriverReviewReason = {
    sk: string;
    period: InsightsPeriod;
    reason: Exclude<ReviewReason, 'Other'>;
    reason_category: 'positive' | 'negative';
    reason_count: number;
    reason_rank: number;
    reason_percentage: number;
};

export type DriverReviewRegionalRanking = {
    sk: string;
    period: InsightsPeriod;
    region_code: string;
    region_name: string;
    total_reviews: number;
    avg_rating: number;
    regional_percentile: number;
};

export type PeakRentalByHour = {
    sk: string;
    period: InsightsPeriod;
    hour_of_day: number;
    hour_label: string;
    rental_count: number;
};

export type PeakRentalByDayOfWeek = {
    sk: string;
    period: InsightsPeriod;
    day_of_week: number;
    day_name: string;
    rental_count: number;
};

export type PeakRentalSummary = {
    sk: string;
    period: InsightsPeriod;
    busiest_day: string;
    busiest_hour: string;
};

export enum DriverDestinationCategory {
    OFFICE_WORK = 'Office/Work',
    SHOPPING = 'Shopping',
    RESTAURANT_DINING = 'Restaurant/Dining',
    ENTERTAINMENT = 'Entertainment',
    MEDICAL = 'Medical',
    OTHER = 'Other',
}

export type DriverDestination = {
    sk: string;
    destination_category: DriverDestinationCategory;
    top_destination_names: string;
    category_percentage: number;
    total_rentals: number;
    rental_count: number;
};

export type TransientDrivers = {
    id: string;
    period: InsightsPeriod;
    repeat_drivers_per_day: number;
    second_time_drivers: number;
    most_frequent_driver_visits: number;
    non_returning_drivers: number;
    total_drivers: number;
    one_time_drivers: number;
    return_drivers: number;
    frequent_drivers: number;
    one_time_pct: number;
    return_pct: number;
    frequent_pct: number;
    dominant_category_label: string;
};

export type TenantUsage = {
    period: TenantInsightsPeriod;
    tenant_id: string;
    tenant_name: string;
    spots: number;
    registered_drivers: number;
    previous_registered_drivers: number;
    trend_pct: number;
    usage: number;
};

export type TenantUsageMonthly = {
    tenant_id: string;
    tenant_name: string;
    period: TenantInsightsPeriod;
    rental_month: string;
    active_drivers: number;
    usage: number;
};

export type PropertyInsightsSummary = {
    // Each section can be null when a lot has no data for it (e.g. no reviews yet).
    driver_destination: { destination_category: DriverDestinationCategory; category_percentage: number }[] | null;
    transient_driver: {
        non_returning_drivers: number;
        repeat_drivers_per_day: number;
        one_time_pct: number;
        return_pct: number;
        frequent_pct: number;
    } | null;
    reviews: {
        avg_rating: number;
        total_reviews: number;
    } | null;
};
